/**
 * Bank Sync Freshness diagnostic card (Commit 1).
 *
 * Explains the Group Dashboard Bank Balance card's freshness badge. Lists
 * every visible bank connection and the two facts that decide whether it can
 * affect the badge: whether its balance is actually included in Cash Now
 * (included_in_dashboard), and whether it's a real, currently-connected feed
 * (status: active vs dormant). Dormant connections (mock seed rows, manual
 * entries, a pending-setup connection) never drag the badge stale.
 *
 * Read-only.
 */

import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtTs(iso) {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

export function BankSyncFreshnessPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);

  var load = async function () {
    setLoading(true);
    try {
      var res = await diagnosticsAPI.bankSyncFreshness();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Bank Sync Freshness</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Every visible bank connection, whether its balance is actually included in the
          Cash Now figure, and whether it's an active feed (only active, included
          connections can set the Bank Balance card's freshness badge). Read-only.
        </p>

        <Button onClick={load} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Run Diagnostic
        </Button>

        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">Connections (visible)</p>
                <p className="text-xl font-bold font-mono">{data.connection_count}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">Included in Cash Now</p>
                <p className="text-xl font-bold font-mono">{data.included_count}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">Drive the freshness badge</p>
                <p className="text-xl font-bold font-mono">{data.active_included_count}</p>
                <p className="text-[10px] text-muted-foreground mt-1">included AND active</p>
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground font-mono">
              computation path: {data.current_computation_path}
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1 pr-2 font-medium">Entity</th>
                    <th className="py-1 pr-2 font-medium">Bank</th>
                    <th className="py-1 pr-2 font-medium">Source</th>
                    <th className="py-1 pr-2 font-medium">Last synced</th>
                    <th className="py-1 pr-2 font-medium">Status</th>
                    <th className="py-1 pr-2 font-medium">In dashboard</th>
                  </tr>
                </thead>
                <tbody>
                  {data.connections.map(function (c) {
                    return (
                      <tr key={c.account_id} className="border-b border-border/40">
                        <td className="py-1 pr-2">{c.entity}</td>
                        <td className="py-1 pr-2 truncate max-w-[160px]" title={c.bank}>{c.bank}</td>
                        <td className="py-1 pr-2">{c.source || '--'}</td>
                        <td className="py-1 pr-2 whitespace-nowrap text-muted-foreground">{fmtTs(c.last_success_at)}</td>
                        <td className="py-1 pr-2">
                          <Badge
                            variant={c.status === 'active' ? 'default' : 'outline'}
                            className="text-[9px]"
                          >
                            {c.status}
                          </Badge>
                        </td>
                        <td className="py-1 pr-2">
                          {c.included_in_dashboard
                            ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-[9px]">included</Badge>
                            : <span className="text-muted-foreground">--</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
